import { useRef, type KeyboardEvent } from "react";
import { Input, type FormFieldControlProps } from "@lendcord/concord";

export const OTP_LENGTH = 6;

type OtpBoxesProps = FormFieldControlProps & {
  value: string;
  onChange: (value: string) => void;
};

// One input per digit. Pasting is disabled on purpose — the user must key in
// every digit individually rather than dropping a copied code in at once.
// FormField clones its single child and injects id/aria/state onto it — like
// PhoneInput/PasswordInput elsewhere in this feature, only the first box gets
// those props so there's exactly one accessible control for the field label.
export function OtpBoxes({ value, onChange, id, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, state }: OtpBoxesProps) {
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, index) => value[index] ?? "");

  function setDigitAt(index: number, digit: string) {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(""));
  }

  function handleChange(index: number, rawValue: string) {
    const digit = rawValue.replace(/\D/g, "").slice(-1);
    setDigitAt(index, digit);
    if (digit && index < OTP_LENGTH - 1) {
      boxRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      boxRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowLeft" && index > 0) {
      boxRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      boxRefs.current[index + 1]?.focus();
    }
  }

  return (
    <div className="flex gap-2">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(element) => {
            boxRefs.current[index] = element;
          }}
          id={index === 0 ? id : undefined}
          aria-invalid={index === 0 ? ariaInvalid : undefined}
          aria-describedby={index === 0 ? ariaDescribedBy : undefined}
          state={state}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => event.preventDefault()}
          onFocus={(event) => event.target.select()}
          inputMode="numeric"
          maxLength={1}
          className="h-11 w-10 text-center text-lg"
          autoComplete="off"
        />
      ))}
    </div>
  );
}
